import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { DataTableColDef } from '../../interfaces/col-def.interface';
import { Sort } from '../../interfaces/sort.interface';
import { SortOrder } from '../../enums/sortorder.enum';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';

@Component({
  selector: 'tcc-mono-data-table',
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss']
})
export class DataTableComponent {
  @Input() public rowData: any[];
  @Input() public columns: DataTableColDef[];
  @Input() public loading: boolean;
  @Input() public noItemsText: string;

  @Input() public checkboxSelectable: boolean = false;
  @Input() public checkedRows: any[] = [];
  @Input() public checkIndex: string =  'id';
  @Input() public isBoldFirstColumn: boolean = true;

  @Input() public itemSize: number = 40;
  @Input() public minBufferPx: number = 1300;
  @Input() public maxBufferPx: number = 1600;
  @Input() public useInfiniteScroll: boolean = true;

  @Input() public displayFooter: boolean = false;
  @Input() public totalNumberOfRows: number = 0;
  @Input() public footerEndText: string | null = null;
  @Input() public footerChange: 'added' | 'removed' | null = null;

  @Input() public sorts: Sort;
  @Output() public sort = new EventEmitter<Sort>();

  @Output() public onRowClicked = new EventEmitter();
  @Output() public onScroll = new EventEmitter();

  @ViewChild('viewport', {static: false}) viewport: CdkVirtualScrollViewport;

  protected sortField: string;
  protected sortOrder: SortOrder;

  public constructor() {
    this.sortField = this.sorts?.sortField;
    this.sortOrder = this.sorts?.sortOrder;
  }

  public rowClicked(row: any): void {
    this.onRowClicked.emit(row);
  }

  public loadData(event: any): void {
    this.onScroll.emit(event);
  }

  public refresh(data: any) {
    this.rowData = data;
  }

  public isChecked(item: any): boolean {
    const index: number = this.checkedRows.findIndex(i => item[this.checkIndex] === i[this.checkIndex]);
    const val = index !== -1;
    return val;
  }

  public check(item: any): void {
    const index = this.checkedRows.findIndex(i => item[this.checkIndex] === i[this.checkIndex]);

    if (index === -1) {
      this.checkedRows.push(item);
    } else {
      this.checkedRows.splice(index, 1);
    }
  }

  public toggleAll(): void {
    if (this.isNoneChecked()) {
      this.rowData.forEach((i: any) => {
        if (!i?.isGroupHeader) {
          this.checkedRows.push(i);
        }
      });
    } else if (this.isSomeChecked()) {
      this.checkedRows.length = 0;
      this.rowData.forEach((i: any) => {
        if (!i?.isGroupHeader) {
          this.checkedRows.push(i);
        }
      });
    } else {
      this.checkedRows.length = 0;
    }
  }

  public isAllChecked(): boolean {
    const selectableRows = (this.rowData || []).filter((i: any) => !i?.isGroupHeader);
    return (
      this.checkedRows?.length === selectableRows.length &&
      selectableRows.length > 0
    );
  }

  public isNoneChecked(): boolean {
    return !this.checkedRows || this.checkedRows?.length === 0;
  }

  public isSomeChecked(): boolean {
    return !this.isAllChecked() && !this.isNoneChecked();
  }

  public onSort(col: DataTableColDef): void {
    if (!col.field && !col.sortField) {
      console.warn('no field to sort on; use sortField property in case when valueGetter is used');
      return;
    }

    const field = col.field || col.sortField;

    if (field !== this.sortField) {
      // if sortField changes, set sortOrder to ASC;

      this.sortField = field;
      this.sortOrder = SortOrder.ASC;
    } else {
      // if sortField is the same, toggle sortOrder

      this.sortOrder = (this.sortOrder === SortOrder.ASC) ? SortOrder.DESC : SortOrder.ASC;
    }

    this.sort.emit({ sortField: this.sortField, sortOrder: this.sortOrder, sortFunction: col.sortFunction });

  }

  public trackByFn(index: number, item: any): string {
    if (!item) {
      return index.toString();
    }
    if (item.isGroupHeader) {
      return item.id ?? `group-${item.groupHeaderLabel ?? index}`;
    }
    return item.id ?? index.toString();
  }
}
